'use client'

import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses } from '../ProcessedEntry/DomainEntry/processedTypes'
import { apiPatch, apiPost, useEmailTypes, useTenants } from './entriesApi'
import type { EmailTypeItem } from './entryTypes'
import { trackEvent } from '@/lib/analytics'
import { ManageEntityView, ManageEntityField, type ManageEntitySubmitResult } from './ManageEntityView'

type EmailTypeForm = { nameCode: string; name: string; description: string; tenantId: string }

const EMPTY_EMAIL_TYPE_FORM: EmailTypeForm = { nameCode: '', name: '', description: '', tenantId: '' }

function formFromEmailType(e: EmailTypeItem): EmailTypeForm {
  return {
    nameCode: e.name_code,
    name: e.name,
    description: e.description ?? '',
    tenantId: e.tenant_id ?? '',
  }
}

export function ManageEmailTypesView({ theme }: { theme: ProcessedTheme }) {
  const c = watchClasses(theme)
  const { emailTypes, loading: emailTypesLoading, refetch: refetchEmailTypes } = useEmailTypes()
  const { tenants, loading: tenantsLoading } = useTenants()

  const onSubmit = async (form: EmailTypeForm, mode: 'create' | 'edit', id: string | number | undefined): Promise<ManageEntitySubmitResult> => {
    const payload = {
      nameCode: form.nameCode,
      name: form.name,
      description: form.description || null,
      tenantId: form.tenantId || null,
    }

    const result = mode === 'create'
      ? await apiPost<{ id: number }>('/email-types', payload)
      : await apiPatch('/email-types', { ...payload, id })

    if (!result.success) {
      const errorMessage = typeof result.error === 'string' ? result.error : 'Failed to save'
      trackEvent({ event_type: 'action', event_name: mode === 'create' ? 'register_email_type_submit' : 'update_email_type_submit', component: 'manage_email_type_form', field_1_key: 'step', field_1_value: 'error', field_2_key: 'message', field_2_value: errorMessage.slice(0, 200) })
      return { success: false, error: errorMessage }
    }

    trackEvent({ event_type: 'action', event_name: mode === 'create' ? 'register_email_type_submit' : 'update_email_type_submit', component: 'manage_email_type_form', field_1_key: 'step', field_1_value: 'success' })
    return { success: true, id: mode === 'create' ? result.data?.id : id }
  }

  return (
    <ManageEntityView<EmailTypeItem, EmailTypeForm>
      theme={theme}
      title="Email Types"
      createLabel="Create Email Type"
      items={emailTypes}
      loading={emailTypesLoading}
      getId={(e) => e.id}
      getName={(e) => e.name}
      getCode={(e) => e.name_code}
      getCreatedAt={(e) => e.created_at}
      getUpdatedAt={(e) => e.updated_at}
      renderListMeta={(e) => (
        <span className={`shrink-0 rounded px-1.5 py-0.5 text-[9px] font-semibold ${c.line} border ${c.faint}`}>
          {e.email_log_count ?? 0} email{e.email_log_count === 1 ? '' : 's'}
        </span>
      )}
      emptyForm={EMPTY_EMAIL_TYPE_FORM}
      formFromItem={formFromEmailType}
      isFormValid={(form) => !!form.nameCode && !!form.name}
      invalidMessage="Fill in Name Code and Name"
      onSubmit={onSubmit}
      onAfterSuccess={refetchEmailTypes}
      renderFields={(form, setForm) => (
        <>
          <ManageEntityField label="Name Code *" theme={theme}>
            <Input className={c.input} value={form.nameCode} onChange={(e) => setForm((p) => p && { ...p, nameCode: e.target.value })} placeholder="sibs-alert" />
          </ManageEntityField>
          <ManageEntityField label="Name *" theme={theme}>
            <Input className={c.input} value={form.name} onChange={(e) => setForm((p) => p && { ...p, name: e.target.value })} />
          </ManageEntityField>
          <ManageEntityField label="Description" theme={theme}>
            <Textarea className={c.input} value={form.description} onChange={(e) => setForm((p) => p && { ...p, description: e.target.value })} />
          </ManageEntityField>
          <ManageEntityField label="Tenant" theme={theme}>
            <select
              className={`w-full rounded border bg-transparent px-2 py-2 text-[13px] ${c.input}`}
              value={form.tenantId}
              onChange={(e) => setForm((p) => p && { ...p, tenantId: e.target.value })}
              disabled={tenantsLoading}
            >
              <option value="">— None —</option>
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </ManageEntityField>
        </>
      )}
    />
  )
}
